import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { HipaaBadge } from '../components/baa/HipaaBadge';
import { FileText, Shield, Pill, AlertTriangle, ArrowRight, Clock, User, Phone, Mail, MapPin, Globe, ChevronLeft, PenLine, Type, CheckCircle, Pen, Send, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGE_NAMES = {
  en: 'English', es: 'Spanish', zh: 'Chinese', vi: 'Vietnamese',
  ko: 'Korean', pt: 'Portuguese', fr: 'French', ar: 'Arabic',
  ru: 'Russian', hi: 'Hindi', ja: 'Japanese', de: 'German',
  it: 'Italian', pl: 'Polish', tl: 'Tagalog', he: 'Hebrew'
};

const TAB_CONFIG = [
  { key: 'procedures', label: 'Post-Op Instructions', icon: FileText, color: 'blue' },
  { key: 'consents', label: 'Informed Consents', icon: Shield, color: 'emerald' },
  { key: 'compliance', label: 'Compliance Forms', icon: Shield, color: 'amber' },
  { key: 'referrals', label: 'Referrals', icon: Send, color: 'indigo' },
  { key: 'medCards', label: 'Med Cards', icon: Pill, color: 'purple' },
  { key: 'problems', label: 'Problems & Emergencies', icon: AlertTriangle, color: 'amber' },
  { key: 'hipaaDocuments', label: 'HIPAA Authorization', icon: Lock, color: 'rose' },
];

const HIPAA_SECTIONS = [
  "I authorize the dental practice named above to provide me with electronic access to my dental health information through the Patient Portal.",
  "Information accessible includes: post-operative care instructions, informed consent documents, medication cards, specialist referral information, and dental emergency/problem guides.",
  "I understand that this portal uses a secure, private link unique to me and that I should not share this link with others.",
  "I understand that my health information is protected under the Health Insurance Portability and Accountability Act (HIPAA) and that the practice maintains safeguards to protect my information.",
  "This authorization remains in effect until I revoke it in writing to the practice.",
  "I acknowledge that I have read and understand this authorization and agree to access my dental health records electronically."
];

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatDateTime(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  } catch {
    return dateStr;
  }
}

// ───── Signature Pad Component ─────
function SignaturePad({ onSignatureChange }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const getCoords = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  }, []);

  const startDrawing = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoords(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  }, [getCoords]);

  const draw = useCallback((e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoords(e);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1e3a5f';
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  }, [isDrawing, getCoords]);

  const stopDrawing = useCallback(() => {
    if (isDrawing && hasDrawn) {
      const canvas = canvasRef.current;
      onSignatureChange(canvas.toDataURL('image/png'));
    }
    setIsDrawing(false);
  }, [isDrawing, hasDrawn, onSignatureChange]);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    onSignatureChange(null);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = 500;
    canvas.height = 150;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  return (
    <div>
      <canvas
        ref={canvasRef}
        className="w-full border-2 border-dashed border-gray-300 rounded-lg cursor-crosshair touch-none bg-white"
        style={{ height: '120px' }}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
        data-testid="hipaa-signature-canvas"
      />
      {hasDrawn && (
        <button onClick={clearSignature} className="text-xs text-red-500 hover:text-red-700 mt-1" data-testid="hipaa-clear-signature">
          Clear Signature
        </button>
      )}
    </div>
  );
}

// ───── HIPAA Consent Gate ─────
function HipaaConsentGate({ practice, patient, token, onSigned, translatedForm }) {
  const [sigMode, setSigMode] = useState('draw'); // 'draw' or 'type'
  const [drawnSig, setDrawnSig] = useState(null);
  const [typedName, setTypedName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const patientName = `${patient?.firstName || ''} ${patient?.lastName || ''}`.trim();
  const practiceAddr = practice?.address;
  let addressStr = '';
  if (practiceAddr && typeof practiceAddr === 'object') {
    addressStr = [practiceAddr.street, practiceAddr.city, practiceAddr.state, practiceAddr.zipCode || practiceAddr.zip].filter(Boolean).join(', ');
  }

  // Use translated form content if available, otherwise English defaults
  const formTitle = translatedForm?.title || 'HIPAA Authorization';
  const formSubtitle = translatedForm?.subtitle || 'Electronic Access to Health Information';
  const formSections = translatedForm?.sections || HIPAA_SECTIONS;
  const agreeText = translatedForm?.agreeText || 'I have read and agree to this HIPAA Authorization for Electronic Access to Health Information.';
  const buttonText = translatedForm?.buttonText || 'Sign & Continue to Portal';

  const canSubmit = agreed && (sigMode === 'draw' ? !!drawnSig : typedName.trim().length >= 2);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/portal/view/${token}/sign-hipaa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signatureData: sigMode === 'draw' ? drawnSig : typedName.trim(),
          signatureType: sigMode
        })
      });
      const data = await res.json();
      if (data.success) {
        onSigned();
      } else {
        setError(data.detail || 'Failed to submit. Please try again.');
      }
    } catch {
      setError('Unable to connect. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="min-h-screen bg-gray-50">
      <PracticeHeader practice={practice} addressStr={addressStr} />
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-testid="hipaa-consent-form">
          {/* Form Header */}
          <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-6 py-5 text-center">
            <Shield className="h-8 w-8 text-blue-300 mx-auto mb-2" />
            <h1 className="text-white font-bold text-lg">{formTitle}</h1>
            <p className="text-slate-300 text-sm mt-1">{formSubtitle}</p>
          </div>

          <div className="p-6 space-y-5">
            {/* Practice & Patient Info */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Practice</p>
                  <p className="font-semibold text-gray-900">{practice?.name}</p>
                  {addressStr && <p className="text-gray-500 text-xs mt-0.5">{addressStr}</p>}
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Patient</p>
                  <p className="font-semibold text-gray-900">{patientName}</p>
                  <p className="text-gray-500 text-xs mt-0.5">Date: {today}</p>
                </div>
              </div>
            </div>

            {/* Authorization Text */}
            <div className="space-y-3">
              {formSections.map((text, i) => (
                <div key={i} className="flex gap-3">
                  <span className="text-blue-600 font-bold text-sm mt-0.5 flex-shrink-0">{i + 1}.</span>
                  <p className="text-gray-700 text-sm leading-relaxed">{text}</p>
                </div>
              ))}
            </div>

            {/* Agreement Checkbox */}
            <label className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200 cursor-pointer" data-testid="hipaa-agree-checkbox">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-blue-900 font-medium">
                {agreeText}
              </span>
            </label>

            {/* Signature Section */}
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Signature</p>
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => setSigMode('draw')}
                  data-testid="hipaa-sig-mode-draw"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    sigMode === 'draw' ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-gray-100 text-gray-600 border border-gray-200'
                  }`}
                >
                  <PenLine className="h-3.5 w-3.5" /> Draw
                </button>
                <button
                  onClick={() => setSigMode('type')}
                  data-testid="hipaa-sig-mode-type"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    sigMode === 'type' ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-gray-100 text-gray-600 border border-gray-200'
                  }`}
                >
                  <Type className="h-3.5 w-3.5" /> Type
                </button>
              </div>

              {sigMode === 'draw' ? (
                <SignaturePad onSignatureChange={setDrawnSig} />
              ) : (
                <input
                  type="text"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  placeholder="Type your full name"
                  className="w-full border-2 border-dashed border-gray-300 rounded-lg px-4 py-3 text-lg focus:border-blue-400 focus:outline-none"
                  style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive", fontSize: '24px' }}
                  data-testid="hipaa-typed-signature"
                />
              )}
            </div>

            {error && <p className="text-red-600 text-sm">{error}</p>}

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              data-testid="hipaa-submit-button"
              className={`w-full py-3 rounded-lg font-semibold text-white transition-colors ${
                canSubmit && !submitting
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {submitting ? 'Submitting...' : buttonText}
            </button>
          </div>
        </div>
      </div>
      <PortalFooter practice={practice} />
    </div>
  );
}


export default function PatientPortalPage() {
  const { token } = useParams();
  const [portalData, setPortalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('procedures');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docContent, setDocContent] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  const [hipaaSigned, setHipaaSigned] = useState(false);

  useEffect(() => {
    loadPortal();
  }, [token]);

  const loadPortal = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/portal/view/${token}`);
      const data = await res.json();
      if (data.success) {
        setPortalData(data.data);
        // Check if HIPAA was already signed
        if (data.data.patient?.hipaaSignedAt) {
          setHipaaSigned(true);
        }
        // Auto-select first tab that has documents
        const firstWithDocs = TAB_CONFIG.find(t => data.data[t.key]?.length > 0);
        if (firstWithDocs) setActiveTab(firstWithDocs.key);
      } else {
        setError(data.detail || 'Unable to load your documents');
      }
    } catch {
      setError('Unable to connect. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const loadDocument = async (type, assignmentId) => {
    setDocLoading(true);
    setDocContent(null);
    try {
      const endpoint = type === 'procedures' ? 'procedure'
        : type === 'consents' ? 'consent'
        : type === 'compliance' ? 'consent'   // compliance forms use the same consent view endpoint
        : type === 'referrals' ? 'referral'
        : type === 'hipaaDocuments' ? 'hipaa'
        : type === 'problems' ? 'problem' : null;

      if (!endpoint) return;

      const res = await fetch(`${BACKEND_URL}/api/portal/view/${token}/${endpoint}/${assignmentId}`);
      const data = await res.json();
      if (data.success) {
        setDocContent(data.data);
      }
    } catch {
      setDocContent({ error: 'Unable to load document content' });
    } finally {
      setDocLoading(false);
    }
  };

  const handleDocClick = (type, doc) => {
    setSelectedDoc({ type, doc });
    if (type !== 'medCards') {
      loadDocument(type, doc.id);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading your documents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <Shield className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2" data-testid="portal-error-title">We couldn't open your documents</h2>
          <p className="text-gray-600">{error}</p>
          <p className="text-sm text-gray-400 mt-4">If you believe this is an error, please contact your dental office.</p>
        </div>
      </div>
    );
  }

  const { patient, practice, procedures, consents, medCards, referrals, problems, hipaaDocuments } = portalData;

  // Show HIPAA gate if not yet signed
  if (!hipaaSigned) {
    return (
      <HipaaConsentGate
        practice={practice}
        patient={patient}
        token={token}
        onSigned={() => setHipaaSigned(true)}
        translatedForm={portalData.hipaaFormTranslated}
      />
    );
  }

  const patientLang = patient?.preferredLanguage || 'en';
  const langName = LANGUAGE_NAMES[patientLang] || patientLang;

  const practiceAddr = practice?.address;
  let addressStr = '';
  if (practiceAddr) {
    if (typeof practiceAddr === 'object') {
      addressStr = [practiceAddr.street, practiceAddr.city, practiceAddr.state, practiceAddr.zip || practiceAddr.zipCode].filter(Boolean).join(', ');
    } else {
      addressStr = String(practiceAddr);
    }
  }

  // Split consents into informed-consents only vs compliance forms (refusal forms)
  const informedConsentsOnly = (consents || []).filter(
    c => !c.isRefusalForm && !c.isXrayRefusalForm
  );
  const complianceFormsOnly = (consents || []).filter(
    c => c.isRefusalForm || c.isXrayRefusalForm
  );

  const tabData = {
    procedures,
    consents: informedConsentsOnly,
    compliance: complianceFormsOnly,
    referrals,
    medCards,
    problems,
    hipaaDocuments,
  };
  const totalDocs =
    (procedures?.length || 0) +
    (consents?.length || 0) +
    (referrals?.length || 0) +
    (medCards?.length || 0) +
    (problems?.length || 0) +
    (hipaaDocuments?.length || 0);

  // Detail view
  if (selectedDoc) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PracticeHeader practice={practice} addressStr={addressStr} />

        <div className="max-w-3xl mx-auto px-4 py-6">
          <button
            onClick={() => { setSelectedDoc(null); setDocContent(null); }}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-4 font-medium"
            data-testid="portal-back-button"
          >
            <ChevronLeft className="h-4 w-4" /> Back to Documents
          </button>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
              <h2 className="text-white font-bold text-lg">
                {selectedDoc.doc.procedureName || selectedDoc.doc.consentName || selectedDoc.doc.problemName || selectedDoc.doc.medCardName || selectedDoc.doc.specialistName || selectedDoc.doc.referralSourceName || selectedDoc.doc.name || 'Document'}
              </h2>
              <p className="text-blue-200 text-sm mt-1">
                {selectedDoc.type === 'referrals' && selectedDoc.doc.specialistType && `${selectedDoc.doc.specialistType} | `}
                {selectedDoc.doc.dentistName && (selectedDoc.doc.dentistName.toLowerCase().startsWith('dr') ? selectedDoc.doc.dentistName : `Dr. ${selectedDoc.doc.dentistName}`)}
                {selectedDoc.doc.referredByName && !selectedDoc.doc.dentistName && `Dr. ${selectedDoc.doc.referredByName}`}
                {selectedDoc.doc.performedDate && ` | ${formatDate(selectedDoc.doc.performedDate)}`}
                {selectedDoc.doc.assignedDate && ` | ${formatDate(selectedDoc.doc.assignedDate)}`}
                {selectedDoc.doc.assignedAt && ` | ${formatDate(selectedDoc.doc.assignedAt)}`}
                {selectedDoc.doc.referralDate && !selectedDoc.doc.assignedAt && ` | ${formatDate(selectedDoc.doc.referralDate)}`}
              </p>
              {docContent?.isSigned && (
                <span className="inline-flex items-center gap-1 mt-2 bg-green-500 bg-opacity-30 text-white text-xs px-2.5 py-1 rounded-full font-semibold">
                  <CheckCircle className="h-3.5 w-3.5" /> Signed
                </span>
              )}
              {patientLang !== 'en' && (
                <span className="inline-flex items-center gap-1 mt-2 bg-white bg-opacity-20 text-white text-xs px-2 py-1 rounded-full">
                  <Globe className="h-3 w-3" /> {langName}
                </span>
              )}
            </div>

            <div className="p-6">
              {docLoading ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  <p className="mt-3 text-gray-500">
                    {patientLang !== 'en' ? `Translating to ${langName}...` : 'Loading...'}
                  </p>
                </div>
              ) : docContent?.error ? (
                <p className="text-red-500 text-center py-4">{docContent.error}</p>
              ) : docContent?.content ? (
                <div data-testid="portal-document-content">
                  <div className="prose max-w-none">
                    {docContent.content.split('\n').map((line, i) => {
                      const trimmed = line.trim();
                      if (!trimmed) return <div key={i} className="h-3" />;
                      if (/^[^\n:]{2,60}:\s*$/.test(trimmed)) {
                        return (
                          <h3 key={i} className="text-blue-800 font-bold text-base mt-5 mb-2 border-b border-blue-100 pb-1">
                            {trimmed}
                          </h3>
                        );
                      }
                      if (trimmed.startsWith('\u2022') || trimmed.startsWith('-') || trimmed.startsWith('*')) {
                        return (
                          <div key={i} className="flex gap-2 ml-2 mb-1">
                            <span className="text-blue-500 mt-0.5">{'\u2022'}</span>
                            <span className="text-gray-700 text-sm leading-relaxed">{trimmed.replace(/^[\u2022\-*]\s*/, '')}</span>
                          </div>
                        );
                      }
                      if (/^\d+[\.\)]/.test(trimmed)) {
                        return <p key={i} className="text-gray-700 text-sm leading-relaxed ml-2 mb-1">{trimmed}</p>;
                      }
                      return <p key={i} className="text-gray-700 text-sm leading-relaxed mb-2">{trimmed}</p>;
                    })}
                  </div>
                  {/* Signature display for signed consents */}
                  {docContent?.isSigned && docContent?.signatureData && (
                    <div className="mt-6 pt-4 border-t border-gray-200" data-testid="portal-consent-signature">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Patient Signature</p>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        {docContent.signatureData.signatureType === 'draw' && docContent.signatureData.signatureData ? (
                          <img src={docContent.signatureData.signatureData} alt="Patient Signature" className="max-h-24" />
                        ) : docContent.signatureData.signatureType === 'type' && docContent.signatureData.signatureData ? (
                          <p className="text-xl text-gray-800" style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }}>
                            {docContent.signatureData.signatureData}
                          </p>
                        ) : null}
                        {docContent.signatureData.signedAt && (
                          <p className="text-xs text-gray-500 mt-2">Signed on {formatDateTime(docContent.signatureData.signedAt)}</p>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Uploaded file display for in-office signed consents */}
                  {docContent?.isInOffice && docContent?.uploadedFile && (
                    <div className="mt-6 pt-4 border-t border-gray-200" data-testid="portal-consent-uploaded-file">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Signed Copy (In-Office)</p>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <a
                          href={`${BACKEND_URL}${docContent.uploadedFile.viewUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <FileText className="h-4 w-4" />
                          View Signed Document
                        </a>
                        <p className="text-xs text-gray-500 mt-2">
                          {docContent.uploadedFile.filename} ({docContent.uploadedFile.sizeMB} MB)
                          {docContent.uploadedFile.uploadedAt && ` — Uploaded ${formatDateTime(docContent.uploadedFile.uploadedAt)}`}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : selectedDoc.type === 'medCards' ? (
                <div className="text-center py-8 text-gray-500">
                  <Pill className="h-10 w-10 mx-auto mb-3 text-purple-400" />
                  <p className="font-medium">{selectedDoc.doc.medCardName}</p>
                  <p className="text-sm mt-1">Med card information assigned on {formatDate(selectedDoc.doc.assignedAt)}</p>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No content available</p>
              )}
            </div>
          </div>
        </div>

        <PortalFooter practice={practice} />
      </div>
    );
  }

  // Main portal view
  return (
    <div className="min-h-screen bg-gray-50">
      <PracticeHeader practice={practice} addressStr={addressStr} />

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Patient Welcome */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6" data-testid="portal-welcome-card">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Welcome, {patient?.firstName}
              </h2>
              <p className="text-gray-500 text-sm">{totalDocs} document{totalDocs !== 1 ? 's' : ''} available</p>
            </div>
          </div>
          {patientLang !== 'en' && (
            <div className="mt-3 flex items-center gap-2 text-sm text-teal-700 bg-teal-50 rounded-lg px-3 py-2">
              <Globe className="h-4 w-4" />
              Documents will be shown in {langName}
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 mb-4 bg-white rounded-xl shadow-sm border border-gray-200 p-1.5 overflow-x-auto" data-testid="portal-tabs">
          {TAB_CONFIG.map(tab => {
            const count = tabData[tab.key]?.length || 0;
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                data-testid={`portal-tab-${tab.key}`}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.key
                    ? `bg-${tab.color}-50 text-${tab.color}-700 shadow-sm`
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {count > 0 && (
                  <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${
                    activeTab === tab.key ? `bg-${tab.color}-100 text-${tab.color}-800` : 'bg-gray-100 text-gray-600'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Document List */}
        <div className="space-y-3" data-testid="portal-document-list">
          {(tabData[activeTab] || []).length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
              <p className="text-gray-400">No {TAB_CONFIG.find(t => t.key === activeTab)?.label.toLowerCase()} found</p>
            </div>
          ) : (
            tabData[activeTab].map(doc => {
              const isReferral = activeTab === 'referrals';
              const isHipaa = activeTab === 'hipaaDocuments';
              const name = doc.procedureName || doc.consentName || doc.medCardName || doc.problemName || (isReferral ? (doc.specialistName || doc.referralSourceName || 'Referral') : null) || (isHipaa ? doc.name : null) || 'Document';
              const date = doc.performedDate || doc.assignedDate || doc.assignedAt || doc.referralDate || doc.createdAt || doc.signedAt;
              const dentist = doc.dentistName || (isReferral ? doc.referredByName : null);
              const tabColor = TAB_CONFIG.find(t => t.key === activeTab)?.color || 'blue';
              const isConsent = activeTab === 'consents' || activeTab === 'compliance';
              const isSigned = isConsent && (doc.status === 'signed' || doc.status === 'signed_in_office');
              const isInOffice = isConsent && doc.status === 'signed_in_office';
              const isHipaaSigned = isHipaa && doc.status === 'signed';

              return (
                <div
                  key={doc.id}
                  className="w-full bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:border-blue-300 hover:shadow-md transition-all text-left group"
                  data-testid={`portal-doc-${doc.id}`}
                >
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => handleDocClick(activeTab, doc)}
                      className="flex-1 min-w-0 text-left"
                    >
                      <h3 className={`font-semibold text-gray-900 group-hover:text-${tabColor}-600 transition-colors`}>
                        {name}
                      </h3>
                      <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                        {date && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {formatDate(date)}
                          </span>
                        )}
                        {dentist && (
                          <span className="flex items-center gap-1">
                            <User className="h-3.5 w-3.5" />
                            {dentist}
                          </span>
                        )}
                        {isReferral && doc.specialistType && (
                          <span className="text-indigo-600 text-xs font-medium">{doc.specialistType}</span>
                        )}
                        {isReferral && doc.urgency && doc.urgency !== 'routine' && (
                          <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                            doc.urgency === 'urgent' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                          }`}>{doc.urgency}</span>
                        )}
                        {isHipaaSigned && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                            <CheckCircle className="h-3 w-3" /> Signed
                          </span>
                        )}
                      </div>
                    </button>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      {isConsent && isSigned && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full" data-testid={`consent-signed-badge-${doc.id}`}>
                          <CheckCircle className="h-3.5 w-3.5" /> {isInOffice ? 'Signed (In-Office)' : 'Signed'}
                        </span>
                      )}
                      {isConsent && !isSigned && doc.signingToken && (
                        <a
                          href={`/consent-sign/${doc.signingToken}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                          data-testid={`consent-sign-btn-${doc.id}`}
                        >
                          <Pen className="h-3.5 w-3.5" /> Sign Now
                        </a>
                      )}
                      <ArrowRight className="h-5 w-5 text-gray-300 group-hover:text-blue-500 transition-colors" />
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <PortalFooter practice={practice} />
    </div>
  );
}

function PracticeHeader({ practice, addressStr }) {
  return (
    <header className="bg-gradient-to-r from-slate-800 to-slate-900 shadow-lg" data-testid="portal-practice-header">
      <div className="max-w-3xl mx-auto px-4 py-5">
        <div className="flex items-center gap-4">
          {practice?.logo ? (
            <img src={practice.logo} alt={practice.name} className="h-12 w-12 rounded-lg object-cover bg-white" />
          ) : (
            <div className="h-12 w-12 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">{practice?.name?.[0] || 'D'}</span>
            </div>
          )}
          <div>
            <h1 className="text-white font-bold text-lg">{practice?.name || 'Dental Practice'}</h1>
            {practice?.baaSigned && <div className="mt-1.5"><HipaaBadge dark testid="portal-hipaa-badge-header" /></div>}
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
              {addressStr && (
                <span className="text-slate-400 text-xs flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {addressStr}
                </span>
              )}
              {practice?.phone && (
                <span className="text-slate-400 text-xs flex items-center gap-1">
                  <Phone className="h-3 w-3" /> {practice.phone}
                </span>
              )}
              {practice?.email && (
                <span className="text-slate-400 text-xs flex items-center gap-1">
                  <Mail className="h-3 w-3" /> {practice.email}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

function PortalFooter({ practice }) {
  return (
    <footer className="mt-12 border-t border-gray-200 bg-white">
      <div className="max-w-3xl mx-auto px-4 py-6 text-center">
        <p className="text-gray-400 text-xs">
          Secure Patient Portal &mdash; {practice?.name}
        </p>
        <p className="text-gray-300 text-xs mt-1">
          This is a secure, private link. Do not share it with others.
        </p>
        {practice?.baaSigned && <div className="mt-3"><HipaaBadge testid="portal-hipaa-badge-footer" /></div>}
      </div>
    </footer>
  );
}
